import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { postMarkets, PostMarketsParams } from '@/lib/api';
import { styled } from '@mui/material/styles';
import TextField from '@/components/TextField/TextField';
import { useMutation } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import SaveIcon from '@/assets/save-floppy-disk.svg?react';
import { AxiosError } from 'axios';
import { ApiError } from '@/lib/types';
import useApiErrorHandler from '@/hooks/useApiErrorHandler';

const CreateMarketModal = ({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}) => {
  const [marketName, setMarketName] = useState('');
  const [description, setDescription] = useState('');
  const { handleError } = useApiErrorHandler();
  const [formErrors, setFormErrors] = useState<{
    [key: string]: string;
  }>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);

  const handleApiError = (error: AxiosError<ApiError>) => {
    const { generalErrors, fieldErrors, unhandledErrors } =
      handleError(error);
    console.log(generalErrors, fieldErrors, unhandledErrors);
    setFormErrors(fieldErrors);
    setGeneralErrors([...generalErrors, ...unhandledErrors]);
  };

  const { mutate, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostMarketsParams
  >({
    mutationFn: postMarkets,
    onSuccess: () => {
      onSave();
    },
    onError: handleApiError,
  });

  useEffect(() => {
    if (!open) {
      setMarketName('');
      setDescription('');
      setFormErrors({});
      setGeneralErrors([]);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (!marketName) {
      setFormErrors({ name: 'Market Name is required' });
      return;
    }
    setFormErrors({});
    setGeneralErrors([]);

    mutate({
      name: marketName,
      description: description,
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create Market"
      generalErrors={generalErrors}
      actions={[
        <Button
          key="close"
          variant="text"
          onClick={handleClose}
          disabled={isPending}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="save"
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          loading={isPending}
          aria-label="Save Button"
        >
          <SaveIcon />
          <FormattedMessage id="save" defaultMessage="Save" />
        </Button>,
      ]}
    >
      <Label>
        <FormattedMessage
          id="marketName"
          defaultMessage="Market Name"
        />
        <Asterix>*</Asterix>
      </Label>
      <StyledTextField
        value={marketName}
        onChange={(e) => {
          setMarketName(e.target.value);
        }}
        placeholder="Type Market Name"
        variant="outlined"
        fullWidth
        error={!!formErrors.name}
        helperText={formErrors.name || ' '}
      />
      <Label topMargin>
        <FormattedMessage
          id="description"
          defaultMessage="Description"
        />
      </Label>
      <StyledTextField
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        placeholder="Type Description"
        variant="outlined"
        fullWidth
        multiline
        rows={4}
        error={!!formErrors.description}
        helperText={formErrors.description || ' '}
      />
    </Modal>
  );
};

const Label = styled('div')<{ topMargin?: boolean }>(
  ({ theme, topMargin = false }) => ({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    marginBottom: theme.spacing(1),
    marginTop: topMargin ? theme.spacing(4) : 0,
  }),
);

const Asterix = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
}));

const StyledTextField = styled(TextField)({
  marginTop: 0,
  marginBottom: 11,
});

export default CreateMarketModal;
